import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Text,
  Button,
  Layer,
  TextInput,
} from 'grommet';
import { FormClose } from 'grommet-icons';
import uniqid from 'uniqid';
import { useAppTheme } from '@Components/Context';
import { AppButton } from '@Components/Control';
import { ProductFormMultipleTextInputs, ProductFormCheckBoxInput } from '@Components/Partial/Product/NewSearch';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
} from '../../SharedComponents';
import { ALLOWLIST_HEADER_MAP } from '../constants';

const EditChannelModal = ({
  small,
  show,
  onClose,
  onSubmit,
  editingChannel,
  loading,
  paymentPending,
}) => {
  const [channelId, setChannelId] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const { theme: { page, button } } = useAppTheme();

  React.useEffect(() => {
    if (editingChannel) {
      setChannelId(editingChannel.id);
    } else {
      setChannelId('');
    }
    setErrorMessage('');
  }, [editingChannel, show]);

  const handleSubmit = () => {
    setErrorMessage('');
    if (!channelId.trim()) {
      setErrorMessage('Channel ID or channel link is required');
      return;
    }

    if (editingChannel && channelId.trim() === editingChannel.id) {
      setErrorMessage('New channel ID or link cannot be the same as the current channel ID');
      return;
    }

    onSubmit({
      value: channelId.trim(),
      previousValue: editingChannel.id,
    });
  };

  const handleClose = () => {
    setChannelId('');
    setErrorMessage('');
    onClose();
  };

  return (
    <>
      {show && (
        <Layer
          onEsc={handleClose}
          onClickOutside={handleClose}
          responsive={false}
          animate={false}
        >
          <Box
            pad="1.5rem"
            width={small ? '90vw' : '30rem'}
            background={page.modalBg}
            gap="1.5rem"
            round="0.5rem"
          >
            <Box direction="row" justify="between" align="center">
              <Text size="1.25rem" weight={600} color={page.modalText}>
                Update Channel
              </Text>
              <Button
                plain
                icon={<FormClose />}
                onClick={handleClose}
                style={{ padding: '0.5rem' }}
              />
            </Box>
            <Box gap="1rem">
              <Box>
                <Text size="0.875rem" color={page.modalText} margin={{ bottom: '0.5rem' }}>
                  Channel ID or Channel Link
                </Text>
                <TextInput
                  value={channelId}
                  onChange={(e) => {
                    setChannelId(e.target.value);
                    setErrorMessage('');
                  }}
                  placeholder="Enter YouTube channel ID or channel link (e.g., UCPMq9VFJ7wStrgd9-bTVqAw or https://www.youtube.com/@channelname)"
                />
                {errorMessage && (
                  <Text size="0.875rem" color="var(--app-error)" margin={{ top: '0.5rem' }}>
                    {errorMessage}
                  </Text>
                )}
              </Box>
            </Box>
            <Box direction="row" gap="1rem" justify="end">
              <AppButton
                overrideHover
                disabled={loading || paymentPending}
                onClick={handleClose}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label="Cancel"
              />
              <AppButton
                overrideHover
                disabled={loading || !channelId.trim() || paymentPending}
                onClick={handleSubmit}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label="Update"
              />
            </Box>
          </Box>
        </Layer>
      )}
    </>
  );
};

EditChannelModal.propTypes = {
  small: PropTypes.bool.isRequired,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editingChannel: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
  }),
  loading: PropTypes.bool,
  paymentPending: PropTypes.bool.isRequired,
};

const AllowlistTable = ({
  small,
  allowlistData,
  onAddChannel,
  onUpdateChannel,
  onDeleteChannel,
  loading,
  paymentPending,
}) => {
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [editingChannel, setEditingChannel] = React.useState(null);
  const [selectedChannels, setSelectedChannels] = React.useState([]);
  const [channelsToAdd, setChannelsToAdd] = React.useState([]);
  const [hasPendingChannelInput, setHasPendingChannelInput] = React.useState(false);
  const { theme: { page, button } } = useAppTheme();

  const { items = [], pageInfo = {} } = allowlistData || {};
  const { totalResults = 0 } = pageInfo;

  const handleChannelsChange = (newChannels) => {
    setChannelsToAdd(newChannels);
  };

  const handleAddChannels = async () => {
    if (channelsToAdd.length === 0) return;

    // Filter out empty strings
    const validChannels = channelsToAdd.filter((ch) => ch && ch.trim());
    if (validChannels.length === 0) return;

    // Validation: Button is disabled when validChannelsCount > 10, but check defensively
    if (validChannels.length > 10) {
      return;
    }

    await onAddChannel(validChannels);
    // Clear the input and close modal after successful add
    setChannelsToAdd([]);
    setShowAddModal(false);
  };

  // Count valid channels (non-empty) for validation
  const validChannelsCount = channelsToAdd.filter((ch) => ch && ch.trim()).length;

  const handleCloseAddModal = () => {
    setChannelsToAdd([]);
    setShowAddModal(false);
  };

  const handleEdit = (channel) => {
    setEditingChannel(channel);
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setEditingChannel(null);
    setShowEditModal(false);
  };

  const handleEditSubmit = async (body) => {
    await onUpdateChannel(body);
    handleEditModalClose();
  };

  const handleDelete = (channelId) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure you want to remove this channel from the allowlist?')) {
      onDeleteChannel([channelId]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedChannels.length === 0) return;

    // eslint-disable-next-line no-alert
    if (window.confirm(`Are you sure you want to remove ${selectedChannels.length} channel(s) from the allowlist?`)) {
      onDeleteChannel(selectedChannels);
      setSelectedChannels([]);
    }
  };

  const handleSelectChannel = (channelId) => {
    if (selectedChannels.includes(channelId)) {
      setSelectedChannels(selectedChannels.filter((id) => id !== channelId));
    } else {
      setSelectedChannels([...selectedChannels, channelId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedChannels.length === items.length) {
      setSelectedChannels([]);
    } else {
      setSelectedChannels(items.map((item) => item.id));
    }
  };

  const renderDataPoint = (uid, value) => {
    const displayValue = value || '-';
    return (
      <Text size="1rem" color={page.tableText}>
        {displayValue}
      </Text>
    );
  };

  const renderActionButtons = (row) => (
    <Box
      width={ALLOWLIST_HEADER_MAP.find(({ uid }) => uid === 'actions').size}
      align="end"
      direction="row"
      gap="0.5rem"
      key={uniqid()}
    >
      <Button
        plain
        onClick={() => handleEdit(row)}
        style={{ textDecoration: 'none' }}
        disabled={paymentPending}
      >
        <Text color={button.primaryText} size="0.875rem">
          Edit
        </Text>
      </Button>
      <Text color={page.tableText} size="0.875rem">|</Text>
      <Button
        plain
        onClick={() => handleDelete(row.id)}
        style={{ textDecoration: 'none' }}
        disabled={paymentPending}
      >
        <Text color="var(--app-error)" size="0.875rem">
          Delete
        </Text>
      </Button>
    </Box>
  );

  const renderTableCell = (uid, row, display, size) => {
    if (uid === 'actions') {
      return renderActionButtons(row);
    }

    return (
      <Box
        key={uniqid()}
        width={size}
        pad={{
          horizontal: small ? '0' : '0.5rem',
          vertical: small ? '0.25rem' : '0',
        }}
        direction="row"
        gap="0.5rem"
      >
        {small && (
          <Text size="0.875rem" color={page.tableHeader}>
            {display}
            :
          </Text>
        )}
        {renderDataPoint(uid, row[uid])}
      </Box>
    );
  };

  const renderTableContents = () => {
    if (loading || !items || items.length === 0) {
      const displayLabel = loading ? 'Loading channels...' : 'No channels in allowlist. Add a channel to get started.';
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          buttonHighlight={page.highlight}
          loading={loading}
          label={displayLabel}
          textColor={page.tableText}
          loaderColor={page.highlight}
        />
      );
    }

    const checkboxWidth = '3%';
    const remainingWidth = 97;

    return (
      <Box fill="horizontal" direction="column">
        {/* Table Header */}
        <Box
          fill="horizontal"
          wrap={false}
          direction="row"
          border={{
            color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
          }}
        >
          {!small && (
            <Box
              width={checkboxWidth}
              pad={{
                horizontal: '0.5rem',
                top: '1rem',
                bottom: '0.5rem',
              }}
            >
              <ProductFormCheckBoxInput
                small={small}
                bgColor={page.inputBg}
                borderColor={page.inputBorder}
                checkmarkColor={page.highlight}
                focusHighlight={page.focusHighlight}
                value={selectedChannels.length === items.length && items.length > 0}
                handleFormValues={handleSelectAll}
                customBoxSize="1.25rem"
                customCheckmarkSize="1.1rem"
              />
            </Box>
          )}
          {ALLOWLIST_HEADER_MAP.map(({ uid, display, size }) => (
            <Box
              key={uid}
              width={small ? size : `${(parseFloat(size) / 100) * remainingWidth}%`}
              pad={{
                horizontal: '0.5rem',
                top: '1rem',
                bottom: '0.5rem',
              }}
            >
              {renderTableHeader(display, small)}
            </Box>
          ))}
        </Box>
        {/* Table Rows */}
        {items.map((row) => (
          <Box
            key={row.id}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder,
              size: '1px',
              style: 'solid',
              side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {!small && (
              <Box
                width={checkboxWidth}
                pad={{ horizontal: '0.5rem' }}
              >
                <ProductFormCheckBoxInput
                  small={small}
                  bgColor={page.inputBg}
                  borderColor={page.inputBorder}
                  checkmarkColor={page.highlight}
                  focusHighlight={page.focusHighlight}
                  value={selectedChannels.includes(row.id)}
                  handleFormValues={() => handleSelectChannel(row.id)}
                  customBoxSize="1.25rem"
                  customCheckmarkSize="1.1rem"
                />
              </Box>
            )}
            {ALLOWLIST_HEADER_MAP.map(({ uid, display, size }) => (
              renderTableCell(uid, row, display, small ? size : `${(parseFloat(size) / 100) * remainingWidth}%`)
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      {/* Channels Table */}
      <TableWrapper
        small={small}
        header="Allowlisted Channels"
        boxPad={tablePadding(small)}
        totalResults={totalResults}
        currentResults={items.length}
        selectedResultsCount={items.length}
        tableControls={(
          <Box direction="row" gap="1rem" justify="end">
            {selectedChannels.length > 0 && (
              <AppButton
                overrideHover
                disabled={loading || paymentPending}
                onClick={handleBulkDelete}
                level="tableControls"
                color={button.primaryText}
                bgColor="var(--app-error)"
                label={`Delete Selected (${selectedChannels.length})`}
              />
            )}
            <AppButton
              overrideHover
              disabled={loading || paymentPending}
              onClick={() => setShowAddModal(true)}
              level="tableControls"
              color={button.primaryText}
              bgColor={button.primaryBg}
              label="+ Add Channels"
            />
          </Box>
        )}
      >
        <Box fill="horizontal" direction="column">
          {renderTableContents()}
        </Box>
      </TableWrapper>
      {/* Add Channels Modal */}
      {showAddModal && (
        <Layer
          onEsc={handleCloseAddModal}
          onClickOutside={handleCloseAddModal}
          responsive={false}
          animate={false}
        >
          <Box
            pad="1.5rem"
            width={small ? '90vw' : '40rem'}
            background={page.modalBg}
            gap="1.5rem"
            round="0.5rem"
          >
            <Box direction="row" justify="between" align="center">
              <Text size="1.25rem" weight={600} color={page.modalText}>
                Add Channels
              </Text>
              <Button
                plain
                icon={<FormClose />}
                onClick={handleCloseAddModal}
                style={{ padding: '0.5rem' }}
              />
            </Box>
            <Box direction="column" gap="0.5rem">
              <Text size="0.875rem" color={page.modalText}>
                Enter YouTube channel IDs or channel links (e.g., UCPMq9VFJ7wStrgd9-bTVqAw or https://www.youtube.com/@channelname)
              </Text>
              <Text size="0.75rem" color={page.modalText} style={{ fontStyle: 'italic' }}>
                You can add up to 10 channels at a time
              </Text>
            </Box>
            <ProductFormMultipleTextInputs
              maxInputs={10}
              value={channelsToAdd}
              handleFormValues={handleChannelsChange}
              onPendingInputChange={setHasPendingChannelInput}
              focusHighlight={page.focusHighlight}
              bgColor={page.inputBg}
              textColor={page.inputText}
              borderColor={page.inputBorder}
              placeholder="Channel ID or channel link (e.g., UCPMq9VFJ7wStrgd9-bTVqAw or https://www.youtube.com/@channelname)"
              buttonAlign="start"
            />
            {validChannelsCount > 10 && (
              <Text size="0.875rem" color="var(--app-error)">
                You can only add up to 10 channels at a time. Please remove some channels.
              </Text>
            )}
            <Box direction="row" gap="1rem" justify="end">
              <AppButton
                overrideHover
                disabled={loading}
                onClick={handleCloseAddModal}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label="Cancel"
                style={{ border: `1px solid ${button.primaryText}` }}
              />
              <AppButton
                overrideHover
                disabled={
                  loading
                  || validChannelsCount === 0
                  || hasPendingChannelInput
                  || validChannelsCount > 10
                  || paymentPending
                }
                onClick={handleAddChannels}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label="Add Channels"
              />
            </Box>
          </Box>
        </Layer>
      )}
      {/* Edit Channel Modal */}
      <EditChannelModal
        small={small}
        show={showEditModal}
        onClose={handleEditModalClose}
        onSubmit={handleEditSubmit}
        editingChannel={editingChannel}
        loading={loading}
      />
    </>
  );
};

AllowlistTable.propTypes = {
  small: PropTypes.bool.isRequired,
  allowlistData: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      kind: PropTypes.string,
      title: PropTypes.string,
    })),
    pageInfo: PropTypes.shape({
      totalResults: PropTypes.number.isRequired,
      resultsPerPage: PropTypes.number,
    }),
  }),
  onAddChannel: PropTypes.func.isRequired,
  onUpdateChannel: PropTypes.func.isRequired,
  onDeleteChannel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  paymentPending: PropTypes.bool.isRequired,
};

export default AllowlistTable;
